import { Alert, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { isAfter, isBefore } from 'date-fns';
import React, { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Navigate,
  useLocation,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import FormFiles from '../../../../components/form/FormFiles';
import StyledButton, {
  BackButton,
  ClearButton,
  DeleteButton,
  EditButton,
  SaveButton,
  SearchButton,
} from '../../../../components/StyledButton';
import { pageKeywords, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useFormItems } from '../../../../hooks/formItems';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import {
  useDeleteExpense,
  usePostExpense,
} from '../../../../hooks/queries/expense';
import useToastHook from '../../../../hooks/ToastHook';
import { EngeneFileObject } from '../../../../models/EngeneFileObject';
import { ExpenseSaveParam } from '../../../../models/gen/expenseSaveParam';
import { FileKind } from '../../../../models/gen/fileKind';
import { MasterKey } from '../../../../models/gen/masterKey';
import { MatterSummary } from '../../../../models/gen/matterSummary';
import { FileUtils } from '../../../../utils/FileUtils';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { useAlertModal } from '../../../modals/alert';
import { useConfirmModal } from '../../../modals/confirm';
import { MatterDetailModal } from '../../../modals/MatterDetail';
import { useSizeContext } from '../../../SizeProvider';
import { ExpenseDetailContext, ExpenseDetailParams } from './ExpenseDetail';
import { SearchMatterModal } from './search-matter';

/**
 * 詳細プロパティ
 */
type DetailProps = PropsWithChildren<{
  /**
   * 新規作成
   */
  create?: boolean;
  matterSummary?: MatterSummary;
  /**
   * 表示モード
   */
  mode: 'edit' | 'view';
}>;

export type DetailState = ExpenseSaveParam & {
  files: Array<EngeneFileObject>;
};

/**
 *
 * @returns
 */
const Detail: React.FunctionComponent<DetailProps> = ({
  children,
  ...props
}) => {
  const { actions, state } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();
  const { toast } = useToastHook();
  const master = useMasterContext();
  const params = useParams<ExpenseDetailParams>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { expense } = useOutletContext<ExpenseDetailContext>();
  const form = useFormContext<DetailState>();
  const [matterSummary, setMatterSummary] = React.useState(
    // create --> props.matterSummary
    // edit/view --> reference.matterSummary
    props.matterSummary ||
      (searchParams.toString().length === 0 &&
      (TypeUtils.isNonNil<MatterSummary>(
        state.pageInfo.MATTER_EXPENSE?.matterSummary
      ) ||
        state.pageInfo.MATTER_EXPENSE?.matterSummary?.workDateFrom !==
          searchParams.get('date') ||
        state.pageInfo.MATTER_EXPENSE?.matterSummary?.matterId !==
          searchParams.get('matterId') ||
        state.pageInfo.MATTER_EXPENSE?.matterSummary?.dailyMatterId !==
          searchParams.get('dailyMatterId'))
        ? undefined
        : expense?.reference?.matterSummary)
  );
  // React.useEffect(
  //   () => () =>
  //     actions.updatePageInfo({ MATTER_EXPENSE: { matterSummary: null } }),
  //   []
  // );
  React.useEffect(() => {
    if (!TypeUtils.isNil(expense?.reference?.matterSummary)) {
      setMatterSummary(expense?.reference?.matterSummary);
    }
  }, [expense?.reference?.matterSummary]);
  /**
   * 現場経費画面から来た場合は pageInfo に設定されている
   */
  const isFromMatterExpense = React.useMemo(
    () =>
      TypeUtils.isNonNil<MatterSummary>(
        state.pageInfo.MATTER_EXPENSE?.matterSummary
      ),
    [state.pageInfo.MATTER_EXPENSE?.matterSummary]
  );

  const {
    FormDatePicker,
    FormInput,
    FormInputNumber,
    FormItem,
    FormSelect,
    FormTextarea,
  } = useFormItems(form);
  const [selectedMatterId, setSelectedMatterId] = React.useState<number>();
  const {
    isOpen: isOpenMatter,
    onClose: onCloseMatter,
    onOpen: onClickMatterName,
  } = useDisclosure();
  const { ConfirmModal, onOpen: onOpenConfirm } =
    useConfirmModal<DetailState>();
  const { AlertModal, onOpen: onOpenAlert } = useAlertModal<DetailState>();
  const deleteExpense = useDeleteExpense();
  const postExpense = usePostExpense();

  const onDelete = React.useCallback<Consumer<string>>(
    (filename) => {
      const deleteFileNames = form.getValues('deleteFileNames') || [];
      if (
        Array.isArray(deleteFileNames) &&
        !deleteFileNames.includes(filename)
      ) {
        deleteFileNames.push(filename);
      }
      form.setValue('deleteFileNames', deleteFileNames);
    },
    [form]
  );

  const selectedExpenseItemId = React.useMemo(() => {
    const value = form.watch('expense.expenseItemId');
    return TypeUtils.isNil(value) ? undefined : parseInt(value.toString());
  }, [form]);
  const isTransportation = React.useMemo(
    () =>
      TypeUtils.isNonNilNumber(selectedExpenseItemId)
        ? master.getBy(MasterKey.ExpenseItems, selectedExpenseItemId)
            ?.expenseType === 2
        : false,
    [selectedExpenseItemId]
  );

  const onValid = React.useCallback<Consumer<DetailState>>(
    (data) => {
      if (TypeUtils.isNilString(data.expense?.date)) {
        toast('利用日を入力してください', 'warning');
        return;
      }
      if (
        TypeUtils.isNilNumber(data.expense?.matterId) &&
        TypeUtils.isNilNumber(data.expense?.dailyMatterId)
      ) {
        if (state.userInfo?.roleOptions?.isOutsourcer) {
          toast('現場を選択してください', 'warning');
          return;
        }
      } else {
        // 現場が選択されている時
        if (TypeUtils.isNilStrict(data.expense?.matterDate)) {
          toast('現場日付を入力してください', 'warning');
          return;
        } else if (
          isBefore(
            Formatter.createDate(data.expense!.matterDate!),
            Formatter.createDate(matterSummary!.workDateFrom!)
          ) ||
          isAfter(
            Formatter.createDate(data.expense!.matterDate!),
            Formatter.createDate(matterSummary!.workDateTo!)
          )
        ) {
          toast('現場日付が現場の作業期間外です', 'warning');
          return;
        }
      }
      if (TypeUtils.isNilNumber(data.expense?.expenseItemId)) {
        toast('費目を選択してください', 'warning');
        return;
      }
      if (isTransportation) {
        if (TypeUtils.isNilString(data.expense?.route)) {
          toast('経路を入力してください', 'warning');
          return;
        }
      } else {
        if (TypeUtils.isNilString(data.expense?.expenseItemDetail)) {
          toast('内容を入力してください', 'warning');
          return;
        }
      }
      if (TypeUtils.isNilStrict(data.expense?.price)) {
        toast('金額を入力してください', 'warning');
        return;
      }
      if (
        master.getBy(MasterKey.ExpenseItems, data.expense!.expenseItemId!)
          ?.receiptRequired &&
        TypeUtils.isNilArray(data.files)
      ) {
        toast('領収書を添付してください', 'warning');
        return;
      }
      onOpenConfirm(data);
    },
    [master, isTransportation, state.userInfo, matterSummary]
  );

  if (
    props.mode === pageKeywords.mode.EDIT &&
    TypeUtils.isNonNilNumber(expense?.result?.expense?.expenseApplyId)
  ) {
    return (
      <Navigate
        replace
        to={`${pagePaths.EXPENSE_DETAIL}/${params.expenseId}/${pageKeywords.mode.VIEW}`}
      />
    );
  }

  return (
    <VStack
      alignItems="start"
      fontSize={sizes.fontSize.rawMessage}
      width={sizes.width.styledMessageList}
    >
      <VStack
        noValidate
        as="form"
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        onSubmit={form.handleSubmit(onValid)}
        padding={2}
        width="100%"
      >
        <FormDatePicker
          formControlProps={{ isRequired: true }}
          label="利用日"
          mode={props.mode}
          name="expense.date"
          onClickClear={(name) => form.setValue(name, '')}
        />
        <FormItem
          control={form.control}
          // 外注は現場が必須
          formControlProps={{
            isRequired: state.userInfo?.roleOptions?.isOutsourcer,
          }}
          label="現場"
          mode={props.mode}
          name="expense.dailyMatterId"
          renderNode={({ field }) => (
            <HStack flexWrap="wrap" justifyContent="space-between">
              <VStack alignItems="start">
                <StyledButton
                  fontSize={sizes.fontSize.rawMessage}
                  hidden={TypeUtils.isNil(matterSummary)}
                  margin="0px !important"
                  onClick={() => {
                    setSelectedMatterId(matterSummary?.matterId);
                    onClickMatterName();
                  }}
                  textAlign="left"
                  variant="link"
                  whiteSpace="normal"
                >
                  {matterSummary?.matterName}
                </StyledButton>
                <Text
                  hidden={TypeUtils.isNil(matterSummary)}
                  margin="0px !important"
                >
                  依頼元: {matterSummary?.customerName}
                </Text>
                <Text hidden={!TypeUtils.isNil(matterSummary)}>―</Text>
              </VStack>
              <HStack>
                <ClearButton
                  isDisabled={isFromMatterExpense}
                  onClick={() => {
                    setMatterSummary(undefined);
                    form.setValue('expense.matterId', undefined);
                  }}
                />
                <SearchButton
                  isDisabled={isFromMatterExpense}
                  onClick={onOpen}
                />
              </HStack>
            </HStack>
          )}
          viewNode={({ field }) => (
            <VStack alignItems="start">
              <StyledButton
                fontSize={sizes.fontSize.rawMessage}
                hidden={TypeUtils.isNil(matterSummary)}
                margin="0px !important"
                onClick={() => {
                  setSelectedMatterId(matterSummary?.matterId);
                  onClickMatterName();
                }}
                variant="link"
              >
                {matterSummary?.matterName}
              </StyledButton>
              <Text
                hidden={TypeUtils.isNil(matterSummary)}
                margin="0px !important"
              >
                依頼元: {matterSummary?.customerName}
              </Text>
              <Text hidden={!TypeUtils.isNil(matterSummary)}>―</Text>
            </VStack>
          )}
        />
        <FormDatePicker
          formControlProps={{
            hidden: state.userInfo?.roleOptions?.isOutsourcer
              ? false // 外注は常に表示
              : TypeUtils.isNil(matterSummary), // SEは現場サマリが設定されてないときのみ非表示
            isRequired:
              state.userInfo?.roleOptions?.isOutsourcer ||
              TypeUtils.isNonNil(matterSummary),
          }}
          label="現場日付"
          mode={
            isFromMatterExpense ||
            TypeUtils.isNonNilString(searchParams.get('date'))
              ? pageKeywords.mode.VIEW
              : props.mode
          }
          name="expense.matterDate"
          onClickClear={(name) => form.setValue(name, '')}
        />

        <FormSelect<number>
          formControlProps={{ isRequired: true }}
          label="費目"
          mode={props.mode}
          name="expense.expenseItemId"
          options={Object.values(master.expenseItem)
            .sort(ListUtils.sort((m) => m.displayOrder))
            .map((expenseItem) => ({
              value: expenseItem.id,
              valueToDisplay: expenseItem.name,
            }))}
          valueConverter={parseInt}
        />
        <FormInput
          formControlProps={{
            hidden: !isTransportation,
            isRequired: isTransportation,
          }}
          inputProps={{
            placeholder: TypeUtils.isNonNilNumber(selectedExpenseItemId)
              ? master.getBy(MasterKey.ExpenseItems, selectedExpenseItemId)
                  ?.hint || ''
              : undefined,
          }}
          label="経路"
          mode={props.mode}
          name="expense.route"
        />
        <FormInput
          formControlProps={{
            hidden: isTransportation,
            isRequired: !isTransportation,
          }}
          inputProps={{
            placeholder: TypeUtils.isNonNilNumber(selectedExpenseItemId)
              ? master.getBy(MasterKey.ExpenseItems, selectedExpenseItemId)
                  ?.hint || ''
              : undefined,
          }}
          label="内容"
          mode={props.mode}
          name="expense.expenseItemDetail"
        />
        <FormInputNumber
          formControlProps={{ isRequired: true }}
          label="金額"
          mode={props.mode}
          name="expense.price"
          numberInputProps={{ min: 0 }}
          numberInputStepperProps={{ hidden: true }}
        />
        <FormFiles
          form={form as any}
          formControlProps={{
            isRequired: TypeUtils.isNonNilNumber(selectedExpenseItemId)
              ? (master.getBy(MasterKey.ExpenseItems, selectedExpenseItemId)
                  ?.receiptRequired as boolean | undefined)
              : false,
          }}
          mode={props.mode}
          label="領収書"
          name="files"
          onDelete={onDelete}
          prefix="領収書"
          startIndex={FileUtils.getNextIndex(
            expense?.result?.attachFiles?.map(
              (attachFile) => attachFile.fileName
            ) || []
          )}
          visibleFilename
        />
        <FormTextarea label="備考" mode={props.mode} name="expense.remarks" />
        <HStack justifyContent="space-evenly" width="100%">
          {!props.create &&
          TypeUtils.isNilNumber(expense?.result?.expense?.expenseApplyId) ? (
            <DeleteButton onClick={() => onOpenAlert(form.getValues())} />
          ) : null}
          {/* {children} */}
          {props.mode === 'edit' ? (
            <SaveButton />
          ) : (
            <EditButton
              isDisabled={
                !TypeUtils.isNilNumber(
                  expense?.result?.expense?.expenseApplyId
                ) ||
                !TypeUtils.isNilNumber(
                  expense?.result?.expense?.monthlyOutsourceInvoiceId
                )
              }
              onClick={() =>
                navigate(
                  `${pagePaths.EXPENSE_DETAIL}/${params.expenseId}/${pageKeywords.mode.EDIT}${location.search}`
                )
              }
            />
          )}
        </HStack>
      </VStack>
      <BackButton onClick={() => navigate(-1)} />
      <MatterDetailModal
        isOpen={isOpenMatter}
        matterId={selectedMatterId}
        onClose={() => {
          setSelectedMatterId(undefined);
          onCloseMatter();
        }}
      />
      <SearchMatterModal
        initialDate={Formatter.date(
          form.getValues('expense.date') ||
            searchParams.get('date') ||
            new Date()
        )}
        isOpen={isOpen}
        onClose={onClose}
        onSelect={(summary) => {
          setMatterSummary(summary);
          form.setValue('expense.matterId', summary.matterId);
        }}
      />
      <ConfirmModal
        isOkDisabled={(state) => TypeUtils.isNil(state)}
        message="経費を保存してもよろしいですか？"
        onOk={(detailState) => {
          const formData = new FormData();
          const saveFileNames: string[] = [];
          detailState!.files
            .filter((receipt) => TypeUtils.isNonNilString(receipt.newFileName))
            .filter(
              (receipt) =>
                !expense?.result?.attachFiles
                  ?.map((attachedFile) => attachedFile.fileName)
                  .includes(receipt.newFileName)
            )
            .forEach((file) => saveFileNames.push(file.newFileName!));
          const metadata: ExpenseSaveParam = {
            expense: {
              ...detailState!.expense,
              dailyMatterId:
                TypeUtils.isNonNilNumber(detailState?.expense?.matterId) &&
                TypeUtils.isNonNil(detailState?.expense?.matterDate)
                  ? undefined
                  : detailState?.expense?.dailyMatterId,
              date: Formatter.date(detailState?.expense?.date, true),
              expenseItemDetail: isTransportation
                ? undefined
                : detailState?.expense?.expenseItemDetail,
              fileAttached: !!expense?.result?.attachFiles,
              matterDate: TypeUtils.isNonNilNumber(
                detailState?.expense?.matterId
              )
                ? Formatter.date(detailState?.expense?.matterDate, true)
                : undefined,
              route: isTransportation ? detailState?.expense?.route : undefined,
              userCodeWorker: state.userInfo?.user_code,
            },
            deleteFileNames: detailState!.deleteFileNames,
            saveFileNames: saveFileNames,
          };
          formData.append(
            'metadata',
            new Blob([JSON.stringify(metadata)], { type: 'application/json' })
          );

          const validate = FileUtils.validate(
            detailState?.files,
            state.appConfig.facilitySettings,
            FileKind.Receipt
          );
          if (TypeUtils.isNonNilString(validate)) {
            toast(validate, 'warning');
            return;
          }
          detailState!.files
            .filter(
              (report) =>
                !expense?.result?.attachFiles
                  ?.map((attachedFile) => attachedFile.fileName)
                  .includes(report.newFileName || report.fileName) &&
                !TypeUtils.isNil(report.file)
            )
            .forEach((file) =>
              formData.append('files', file.file!, file.fileName)
            );
          actions.setGuard(true);
          postExpense.mutate(formData, {
            onSuccess: () => {
              navigate(-1);
              actions.setGuard(false);
            },
            onError: (error) => {
              toast(
                error instanceof AppError ? error : '経費保存に失敗しました'
              );
              actions.setGuard(false);
            },
          });
        }}
      />
      <AlertModal
        message="経費を削除してもよろしいですか？"
        isOkDisabled={(detail) =>
          TypeUtils.isNilNumber(detail?.expense?.expenseId) ||
          TypeUtils.isNilNumber(detail?.expense?.expenseVersion)
        }
        onOk={() => {
          actions.setGuard(true);
          deleteExpense.mutate(
            {
              id: expense.result?.expense?.expenseId,
              version: expense.result?.expense?.expenseVersion,
            },
            {
              onSuccess: () => {
                // form.reset({});
                navigate(-1);
                actions.setGuard(false);
              },
              onError: (error) => {
                toast(
                  error instanceof AppError ? error : '経費削除に失敗しました'
                );
                actions.setGuard(false);
              },
            }
          );
        }}
      />
    </VStack>
  );
};

export default Detail;
